// hooks/useCategorys.js
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getUsers,
    editUser,
    removeUser,
    createAdmin,
    createDelivery,
    addAreasService,
    toggleBlockUserService,
    adminChangeUserPasswordService,
    getUsersDropdown,
    getDriverWallet,
    topUpDriverWallet,
    deductDriverWallet,
    getDriverPerformance,
} from "services/user";
import { setUsers } from "store/userSlice";
import { useSearchParams } from "react-router-dom";
import { readLoadError } from "utils/apiError";

const useUser = (fixedRole = null) => {
    const users = useSelector((state) => state.user.users);
    const page = useSelector((state) => state.user.page);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();

    const [params, setParams] = useState({});
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const newParams = {};
        newParams.page = page;
        newParams.size = 10;

        if (searchParams.get("search")) {
            newParams.search = searchParams.get("search");
        }
        if (fixedRole) {
            newParams.role = fixedRole;
        } else if (searchParams.get("role")) {
            newParams.role = searchParams.get("role");
        }

        setParams(newParams);
    }, [fixedRole, page, searchParams]);

    // Fetch Categorys
    const fetchUsers = async (params) => {
        setLoading(true);
        try {
            const data = await getUsers(params);
            dispatch(setUsers(data));
            return data;
        } catch (err) {
            setError(err.message || "Failed to fetch users");
        } finally {
            setLoading(false);
        }
    };

    // Create Category
    const addAdmin = async (newAdmin) => {
        setLoading(true);
        try {
            const response = await createAdmin(newAdmin);
            fetchUsers(params);
            return response;
        } catch (err) {
            setError(err.response.data?.message || "Failed to create admin");
            return err;
        } finally {
            setLoading(false);
        }
    };

    // Edit Category
    const addDelivery = async (updatedData) => {
        setLoading(true);
        try {
            const updated = await createDelivery(updatedData);

            fetchUsers(params);

            return updated;
        } catch (err) {
            setError(err.response.data.message || "Failed to add delivery");
            return err;
        } finally {
            setLoading(false);
        }
    };

    const addAreas = async (data) => {
        setLoading(true);
        try {
            const updated = await addAreasService(data);

            fetchUsers(params);

            return updated;
        } catch (err) {
            setError(err.response.data.message || "Failed to add delivery");
            return err;
        } finally {
            setLoading(false);
        }
    };

    const updateUser = async (updatedData) => {
        setLoading(true);
        try {
            const updated = await editUser(updatedData);
            fetchUsers(params);
            return updated;
        } catch (err) {
            setError(err.message || "Failed to update user");
        } finally {
            setLoading(false);
        }
    };
    const updateFcm = async (fcm) => {
        setLoading(true);
        try {
            const updated = await editUser({ fcm });
            return updated;
        } catch (err) {
            setError(err.message || "Failed to update user");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Deletes a user and refreshes the list.
     *
     * Returns the API's response rather than `true`: deleting a vendor here
     * goes through the same count-first path as the restaurants screen, so the
     * answer is `{ deleted, message }` — the store may have been archived
     * instead of destroyed, and the caller has to say which.
     *
     * Throws on failure. It used to RETURN the error object, which is truthy,
     * so the screen's `if (response)` reported "تم حذف الحساب بنجاح" over a
     * delete the database had just refused.
     */
    const deleteUser = async (id) => {
        setLoading(true);
        try {
            const result = await removeUser(id);

            fetchUsers(params);

            return result;
        } catch (err) {
            setError(err.message || "Failed to delete user");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /** Toggle isBlocked for a user and refresh the user list. */
    const toggleBlockUser = async (id) => {
        setLoading(true);
        try {
            const response = await toggleBlockUserService(id);
            fetchUsers(params);
            return response;
        } catch (err) {
            setError(err.response?.data?.message || "Failed to toggle block status");
            return err;
        } finally {
            setLoading(false);
        }
    };

    /** Admin sets a new password for the given user. */
    const changeUserPassword = async (id, password, confirmPassword) => {
        setLoading(true);
        try {
            const response = await adminChangeUserPasswordService(id, password, confirmPassword);
            return response;
        } catch (err) {
            setError(err.response?.data?.message || "Failed to change password");
            return err;
        } finally {
            setLoading(false);
        }
    };

    return {
        users,
        loading,
        error,
        fetchUsers,
        addAdmin,
        addDelivery,
        deleteUser,
        updateUser,
        updateFcm,
        addAreas,
        toggleBlockUser,
        changeUserPassword,
    };
};

export default useUser;

// --- The people list, on TanStack Query ---

export const userKeys = {
    all: ["users"],
    list: (filters) => [...userKeys.all, filters],
};

/**
 * The customers, drivers and admins pages, all three.
 *
 * WHY THIS REPLACED THE REDUX LIST. The three pages are one component behind
 * three routes, and they read one shared slice. So on a route change the new
 * page rendered the PREVIOUS page's accounts until its own request landed —
 * customer names under the drivers columns, for as long as the fetch took. The
 * rows were never wrong for long, which is why it read as a glitch rather than
 * as a bug.
 *
 * The role is part of the query key, so each page now has its own cache entry.
 * Going back to a page you have already opened paints it from cache with no
 * request at all, which is the difference between "smooth" and "correct but
 * still blinking".
 *
 * THE `placeholderData` RULE IS THE POINT, and it is not the vendors one.
 * Carrying the previous data forward is what keeps the list — and the search
 * box — mounted while a page or a search changes, so typing does not lose
 * focus. Carried across ROLES it would put the old page's accounts back on
 * screen, which is the bug this hook exists to remove. So it is reused only
 * when the previous query was for the same role.
 */
export const useGetUsers = ({
    role,
    page,
    size = 10,
    search = "",
    appStatus = "",
    cityId = "",
    sortBy = "",
    sortDir = "",
    adminType = "",
    hasNote = "",
}) => {
    const filters = {
        role,
        page,
        size,
        search,
        appStatus,
        cityId,
        sortBy,
        sortDir,
        adminType,
        hasNote,
    };

    const query = useQuery({
        queryKey: userKeys.list(filters),
        queryFn: () => {
            const params = { page, size, role };
            if (search) params.search = search;
            if (appStatus) params.appStatus = appStatus;
            if (cityId) params.cityId = cityId;
            if (sortBy) {
                params.sortBy = sortBy;
                params.sortDir = sortDir || "asc";
            }
            if (adminType) params.adminType = adminType;
            if (hasNote) params.hasNote = hasNote;
            return getUsers(params);
        },
        placeholderData: (previousData, previousQuery) => {
            // queryKey is [...userKeys.all, filters], so the filters object is
            // the last element whatever the prefix grows into.
            const previousFilters = previousQuery?.queryKey?.at(-1);
            return previousFilters?.role === role ? previousData : undefined;
        },
        // The driver app column ages in real time. 30s keeps it honest against
        // the 60s "مفتوح" threshold without hammering the list.
        refetchInterval: 30_000,
    });

    return {
        users: query.data?.results ?? [],
        pages: query.data?.pages ?? 1,
        total: query.data?.count ?? 0,
        // isLoading is true only when there is no data to show at all, so a
        // background refetch never blanks the table.
        loading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error?.message ?? null,
        // The sentence for the page when the list did not load.
        loadError: readLoadError(query.error),
        refetch: query.refetch,
    };
};

// --- Driver wallet -------------------------------------------------------

export const walletKeys = {
    all: ["driver-wallet"],
    detail: (userId) => [...walletKeys.all, userId],
};

/**
 * One driver's balance and ledger.
 *
 * `enabled` on the id so the query does not fire before a driver is chosen.
 * No polling: a wallet moves when somebody acts, and both actions that move it
 * — a top-up here, a claim by the driver — invalidate it.
 */
export const useGetDriverWallet = (userId, page = 1) => {
    const query = useQuery({
        queryKey: [...walletKeys.detail(userId), page],
        queryFn: () => getDriverWallet(userId, { page, size: 10 }),
        enabled: Boolean(userId),
    });

    return {
        balance: query.data?.balance ?? null,
        transactions: query.data?.results ?? [],
        pages: query.data?.pages ?? 1,
        loading: query.isLoading,
        // The shared wallet panel shows "updating" and a retry, the same as
        // the restaurant wallet.
        isFetching: query.isFetching,
        error: query.error ? query.error.response?.data?.message || "تعذر تحميل المحفظة" : null,
        refetch: query.refetch,
    };
};

/** Both the wallet screen and the balance column on the list. */
const invalidateDriverWallet = (queryClient, userId) => {
    queryClient.invalidateQueries({ queryKey: walletKeys.detail(userId) });
    queryClient.invalidateQueries({ queryKey: userKeys.all });
};

export const useTopUpDriverWallet = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, amount, note, idempotencyKey }) =>
            topUpDriverWallet(userId, { amount, note, idempotencyKey }),
        onSuccess: (_data, variables) => invalidateDriverWallet(queryClient, variables.userId),
    });
};

export const useDeductDriverWallet = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userId, amount, note, idempotencyKey }) =>
            deductDriverWallet(userId, { amount, note, idempotencyKey }),
        onSuccess: (_data, variables) => invalidateDriverWallet(queryClient, variables.userId),
    });
};

// --- Lightweight dropdown hook ---

export const userDropdownKeys = {
    all: ["users-dropdown"],
    list: (params) => [...userDropdownKeys.all, { params }],
};

export const useGetUsersDropdown = (params = {}, options = {}) => {
    const query = useQuery({
        queryKey: userDropdownKeys.list(params),
        queryFn: async () => {
            const data = await getUsersDropdown(params);
            return data.results;
        },
        enabled: options.enabled ?? true,
    });

    return {
        users: query.data ?? [],
        loading: query.isLoading,
        error: query.error?.message ?? null,
    };
};

export const driverPerformanceKeys = {
    all: ["driver-performance"],
    detail: (userId, window) => [...driverPerformanceKeys.all, userId, window],
};

/** Read the last 30 days for a driver, with a small cache per table row. */
export const useGetDriverPerformance = (userId, window = "30d") => {
    const query = useQuery({
        queryKey: driverPerformanceKeys.detail(userId, window),
        queryFn: () => getDriverPerformance(userId, { window }),
        enabled: Boolean(userId),
        staleTime: 60_000,
    });

    return {
        performance: query.data ?? null,
        loading: query.isLoading,
        error: query.error?.message ?? null,
    };
};
